import React, { useState, useEffect } from 'react';
import {
  aiClinicalNotesService,
  ClinicalNote,
  ClinicalNoteSuggestion,
  NoteTemplate,
  GenerateNoteOptions,
  NoteSections
} from '../services/AIClinicalNotesService';
import { useToast } from './Toast';
import VoiceInputButton from './VoiceInputButton';
import PDFGeneratorService from '../services/PDFGeneratorService';

export interface ClinicalNotesEditorProps {
  sessionId: string;
  patientId: string;
  providerId: string;
  transcript?: string;
  sessionType?: string;
  sessionDuration?: number;
  patientHistory?: {
    diagnoses?: string[];
    medications?: string[];
    previousNotes?: string[];
  };
  onNoteSaved?: (note: ClinicalNote) => void;
  onClose?: () => void;
}

// Define section fields for each template type
const TEMPLATE_SECTIONS: Record<string, { key: keyof NoteSections; label: string; placeholder: string }[]> = {
  SOAP: [
    { key: 'subjective', label: 'Subjective Complaint', placeholder: "Client's self-reported feelings, emotions, concerns, and symptoms..." },
    { key: 'objective', label: 'Objective Findings', placeholder: 'Clinical observations: appearance, behavior, affect, speech, eye contact...' },
    { key: 'assessment', label: 'Assessment of Progress', placeholder: 'Diagnosis, clinical impressions, progress toward goals...' },
    { key: 'plan', label: 'Plans for Next Session', placeholder: 'Treatment continuation, homework, referrals, next session focus...' },
  ],
  DAP: [
    { key: 'data', label: 'Data', placeholder: 'Session facts: what the client reported, what you observed, interventions used...' },
    { key: 'assessment', label: 'Assessment', placeholder: 'Clinical interpretation of the data, progress, diagnostic impressions...' },
    { key: 'plan', label: 'Plan', placeholder: 'Next steps, homework, referrals, treatment adjustments...' },
  ],
  BIRP: [
    { key: 'behavior', label: 'Behavior', placeholder: 'Observable client behaviors during session: mood, affect, engagement...' },
    { key: 'intervention', label: 'Intervention', placeholder: 'Therapeutic techniques and interventions used during the session...' },
    { key: 'response', label: 'Response', placeholder: "Client's response to interventions: receptivity, insight, resistance..." },
    { key: 'plan', label: 'Plan', placeholder: 'Homework, follow-up actions, next session goals...' },
  ],
  Progress: [
    { key: 'progress', label: 'Progress Summary', placeholder: 'Overall progress toward treatment goals since last session...' },
    { key: 'goals', label: 'Treatment Goals Addressed', placeholder: 'Which goals were worked on, current status of each...' },
    { key: 'barriers', label: 'Barriers to Progress', placeholder: 'Obstacles, challenges, or setbacks encountered...' },
    { key: 'nextSteps', label: 'Next Steps', placeholder: 'Planned interventions, homework, session frequency adjustments...' },
  ],
  Initial: [
    { key: 'presentingProblem', label: 'Presenting Problem', placeholder: 'Chief complaint, reason for seeking treatment, symptom onset and duration...' },
    { key: 'history', label: 'Relevant History', placeholder: 'Mental health history, medical history, family history, substance use...' },
    { key: 'mentalStatus', label: 'Mental Status Exam', placeholder: 'Appearance, behavior, mood, affect, thought process, cognition, insight, judgment...' },
    { key: 'diagnosis', label: 'Diagnostic Impression', placeholder: 'ICD-10 codes, differential diagnosis, clinical rationale...' },
    { key: 'recommendations', label: 'Recommendations', placeholder: 'Treatment recommendations, frequency, referrals, medication considerations...' },
  ],
};

const TEMPLATE_LABELS: Record<string, string> = {
  SOAP: 'SOAP Note',
  DAP: 'DAP Note',
  BIRP: 'BIRP Note',
  Progress: 'Progress Note',
  Initial: 'Initial Assessment',
};

const ClinicalNotesEditor: React.FC<ClinicalNotesEditorProps> = ({
  sessionId,
  patientId,
  providerId,
  transcript = '',
  sessionType,
  sessionDuration,
  patientHistory,
  onNoteSaved,
  onClose
}) => {
  const toast = useToast();
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>('SOAP');
  const [sectionData, setSectionData] = useState<Record<string, Record<string, string>>>({});
  const [aiSuggestion, setAiSuggestion] = useState<ClinicalNoteSuggestion | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingNote, setExistingNote] = useState<ClinicalNote | null>(null);
  const [customInstructions, setCustomInstructions] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [noteDate, setNoteDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOverride, setExportOverride] = useState({
    practiceName: '',
    providerName: '',
    credentials: '',
    address: '',
    phone: '',
    npiNumber: '',
    licenseNumber: '',
    dateOfService: '',
  });

  // Load existing note if available
  useEffect(() => {
    loadExistingNote();
  }, [sessionId]);

  const loadExistingNote = async () => {
    const notes = await aiClinicalNotesService.getNotesBySession(sessionId);
    if (notes.length > 0) {
      const note = notes[0];
      setExistingNote(note);
      setExpandedTemplate(note.template);

      // Parse existing content back into sections
      const parsed = parseContentToSections(note.content, note.template);
      setSectionData(prev => ({ ...prev, [note.template]: parsed }));

      if (note.aiGeneratedContent) {
        setAiSuggestion({
          template: note.template,
          content: note.aiGeneratedContent,
          sections: {},
          confidence: 1,
          sources: [],
          generatedAt: note.createdAt
        });
      }
    }
  };

  // Parse flat content string back into section fields
  // Handles multiple formats: "## Subjective", "SUBJECTIVE:", "--- SUBJECTIVE:", "Subjective Complaint:", etc.
  const parseContentToSections = (content: string, template: string): Record<string, string> => {
    const sections = TEMPLATE_SECTIONS[template];
    if (!sections) return {};

    // Build header aliases for matching
    const headerAliases: Record<string, string[]> = {
      subjective: ['SUBJECTIVE', 'SUBJECTIVE COMPLAINT', 'S:'],
      objective: ['OBJECTIVE', 'OBJECTIVE FINDINGS', 'O:'],
      assessment: ['ASSESSMENT', 'ASSESSMENT OF PROGRESS', 'A:'],
      plan: ['PLAN', 'PLANS FOR NEXT SESSION', 'P:'],
      data: ['DATA', 'D:'],
      behavior: ['BEHAVIOR', 'B:'],
      intervention: ['INTERVENTION', 'I:'],
      response: ['RESPONSE', 'R:'],
      progress: ['PROGRESS', 'PROGRESS SUMMARY'],
      goals: ['GOALS', 'TREATMENT GOALS', 'TREATMENT GOALS ADDRESSED'],
      barriers: ['BARRIERS', 'BARRIERS TO PROGRESS'],
      nextSteps: ['NEXT STEPS'],
      presentingProblem: ['PRESENTING PROBLEM'],
      history: ['RELEVANT HISTORY', 'HISTORY'],
      mentalStatus: ['MENTAL STATUS', 'MENTAL STATUS EXAM', 'MSE'],
      diagnosis: ['DIAGNOSIS', 'DIAGNOSTIC IMPRESSION'],
      recommendations: ['RECOMMENDATIONS'],
    };

    const result: Record<string, string> = {};
    const lines = content.split('\n');
    let currentKey = '';
    let currentLines: string[] = [];

    for (const line of lines) {
      const trimmed = line.replace(/^[\s\-#*]+/, '').replace(/[*]+$/, '').trim();
      let matchedKey = '';

      // Check against each section
      for (const section of sections) {
        const aliases = [section.label.toUpperCase(), ...(headerAliases[section.key] || [])];
        for (const alias of aliases) {
          if (trimmed.toUpperCase().startsWith(alias)) {
            // Verify this looks like a header (followed by : or end of line)
            const afterAlias = trimmed.slice(alias.length).trim();
            if (afterAlias === '' || afterAlias.startsWith(':') || afterAlias.startsWith('—') || afterAlias.startsWith('-')) {
              matchedKey = section.key;
              break;
            }
          }
        }
        if (matchedKey) break;
      }

      if (matchedKey) {
        if (currentKey) {
          result[currentKey] = currentLines.join('\n').trim();
        }
        currentKey = matchedKey;
        // Get any content after the header on the same line
        const colonIdx = line.indexOf(':');
        const afterColon = colonIdx !== -1 ? line.slice(colonIdx + 1).trim() : '';
        currentLines = afterColon ? [afterColon] : [];
      } else if (currentKey) {
        currentLines.push(line);
      }
    }
    if (currentKey) {
      result[currentKey] = currentLines.join('\n').trim();
    }

    // If parsing failed (no sections found), put everything in the first section
    if (Object.keys(result).length === 0 && content.trim() && sections.length > 0) {
      result[sections[0].key] = content.trim();
    }

    return result;
  };

  // Build flat content from section data
  const buildContentFromSections = (template: string): string => {
    const sections = TEMPLATE_SECTIONS[template];
    const data = sectionData[template] || {};
    if (!sections) return '';

    return sections
      .map(s => `${s.label}:\n${data[s.key] || ''}`)
      .join('\n\n');
  };

  const handleSectionChange = (template: string, sectionKey: string, value: string) => {
    setSectionData(prev => ({
      ...prev,
      [template]: {
        ...(prev[template] || {}),
        [sectionKey]: value
      }
    }));
  };

  const toggleTemplate = (template: string) => {
    setExpandedTemplate(prev => prev === template ? null : template);
  };

  const handleGenerateAI = async () => {
    if (!transcript || transcript.trim().length === 0) {
      toast.warning('No Transcript', 'No transcript available. Cannot generate AI suggestion.');
      return;
    }

    if (!expandedTemplate) {
      toast.warning('Select Note Type', 'Please expand a note type first.');
      return;
    }

    setIsGenerating(true);

    try {
      const options: GenerateNoteOptions = {
        transcript,
        patientHistory,
        template: expandedTemplate as NoteTemplate,
        customInstructions: customInstructions || undefined,
        sessionType,
        sessionDuration
      };

      const suggestion = await aiClinicalNotesService.generateNoteSuggestion(options);
      setAiSuggestion(suggestion);

      // Parse AI content into sections and populate fields
      if (suggestion.sections && Object.keys(suggestion.sections).length > 0) {
        const sectionValues: Record<string, string> = {};
        for (const [key, value] of Object.entries(suggestion.sections)) {
          if (value) sectionValues[key] = value;
        }
        setSectionData(prev => ({ ...prev, [expandedTemplate]: sectionValues }));
      } else {
        // Parse from content string
        const parsed = parseContentToSections(suggestion.content, expandedTemplate);
        setSectionData(prev => ({ ...prev, [expandedTemplate]: parsed }));
      }

    } catch (error) {
      toast.error('AI Suggestion Failed', 'Failed to generate AI suggestion. Please try again or write manually.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async (status: 'draft' | 'signed' = 'draft') => {
    if (!expandedTemplate) {
      toast.warning('Select Note Type', 'Please expand a note type first.');
      return;
    }

    const content = buildContentFromSections(expandedTemplate);
    if (!content.trim() || !hasAnySectionContent(expandedTemplate)) {
      toast.warning('Empty Note', 'Please fill in at least one section.');
      return;
    }

    setIsSaving(true);

    try {
      let savedNote: ClinicalNote;

      if (existingNote) {
        savedNote = await aiClinicalNotesService.updateNote(
          existingNote.id,
          content,
          providerId
        );
      } else {
        savedNote = await aiClinicalNotesService.createNote(
          sessionId,
          patientId,
          providerId,
          expandedTemplate as NoteTemplate,
          content,
          aiSuggestion !== null,
          aiSuggestion?.content
        );
      }

      if (status === 'signed') {
        savedNote = await aiClinicalNotesService.signNote(savedNote.id, providerId);
      }

      setExistingNote(savedNote);
      toast.success('Note Saved', status === 'signed' ? 'Note signed and finalized.' : 'Note saved as draft.');
      onNoteSaved?.(savedNote);

    } catch (error) {
      toast.error('Save Failed', 'Failed to save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async (useOverride: boolean) => {
    if (!expandedTemplate) return;
    const data = sectionData[expandedTemplate] || {};
    const sections = TEMPLATE_SECTIONS[expandedTemplate];
    try {
      const pdfService = PDFGeneratorService.getInstance();
      const practiceInfo = await PDFGeneratorService.fetchPracticeInfo(providerId);

      // Pre-fill override fields from practice info on first open
      if (!exportOverride.practiceName && !useOverride) {
        setExportOverride({
          practiceName: practiceInfo.practiceName,
          providerName: practiceInfo.providerName,
          credentials: practiceInfo.credentials,
          address: practiceInfo.address,
          phone: practiceInfo.phone,
          npiNumber: practiceInfo.npiNumber,
          licenseNumber: practiceInfo.licenseNumber,
          dateOfService: noteDate,
        });
      }

      const headerOverride = useOverride ? {
        practiceName: exportOverride.practiceName || undefined,
        providerName: exportOverride.providerName || undefined,
        credentials: exportOverride.credentials || undefined,
        address: exportOverride.address || undefined,
        phone: exportOverride.phone || undefined,
        npiNumber: exportOverride.npiNumber || undefined,
        licenseNumber: exportOverride.licenseNumber || undefined,
        dateOfService: exportOverride.dateOfService || undefined,
      } : undefined;

      const blob = pdfService.generateClinicalNotePDF({
        template: expandedTemplate,
        sections: data,
        sectionLabels: sections.map(s => ({ key: s.key, label: s.label })),
        patientName: patientId, // Will be replaced with actual name if available
        sessionDate: noteDate,
        status: existingNote?.status === 'signed' ? 'signed' : 'draft',
        signedAt: existingNote?.signedAt ? new Date(existingNote.signedAt).toLocaleString() : undefined,
        version: existingNote?.version || 1,
        headerOverride,
        practiceInfo,
      });

      const filename = `Clinical_Note_${expandedTemplate}_${noteDate}.pdf`;
      pdfService.downloadPDF(blob, filename);
      setShowExportModal(false);
      toast.success('PDF Exported', 'Clinical note PDF downloaded.');
    } catch (err) {
      toast.error('Export Failed', 'Failed to generate PDF.');
    }
  };

  const hasAnySectionContent = (template: string): boolean => {
    const data = sectionData[template] || {};
    return Object.values(data).some(v => v && v.trim().length > 0);
  };

  const getWordCount = (template: string): number => {
    const data = sectionData[template] || {};
    return Object.values(data)
      .join(' ')
      .split(/\s+/)
      .filter(w => w.length > 0).length;
  };

  const templateKeys = Object.keys(TEMPLATE_SECTIONS);

  return (
    <div style={{
      background: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      border: '1px solid #374151',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: '2px solid #374151',
        paddingBottom: '16px'
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#F7E9A0', fontSize: '1.5rem' }}>
            Clinical Notes
          </h2>
          {existingNote && (
            <span style={{ fontSize: '13px', color: '#d1d5db' }}>
              Version {existingNote.version} &middot; {existingNote.status === 'signed' ? 'Signed' : 'Draft'}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Note date */}
          <label style={{ color: '#d1d5db', fontSize: '13px' }}>Date:</label>
          <input
            type="date"
            value={noteDate}
            onChange={(e) => setNoteDate(e.target.value)}
            style={{
              background: '#374151',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              padding: '6px 10px',
              color: '#f9fafb',
              fontSize: '13px',
              colorScheme: 'dark'
            }}
          />
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: '#6b7280',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* AI Controls */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <button
          onClick={handleGenerateAI}
          disabled={isGenerating || !transcript}
          style={{
            background: isGenerating ? '#6b7280' : '#D4A017',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            color: '#1f2937',
            fontSize: '14px',
            cursor: isGenerating || !transcript ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            opacity: isGenerating || !transcript ? 0.5 : 1
          }}
        >
          {isGenerating ? 'Generating...' : 'Generate AI Suggestion'}
        </button>

        <button
          onClick={() => setShowInstructions(!showInstructions)}
          style={{
            background: '#374151',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            padding: '8px 16px',
            color: '#f9fafb',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Custom Instructions
        </button>

        {!transcript && (
          <span style={{ color: '#d1d5db', fontSize: '13px', fontStyle: 'italic' }}>
            No transcript available — write notes manually
          </span>
        )}
      </div>

      {/* Custom Instructions Panel */}
      {showInstructions && (
        <div style={{
          background: '#28527A',
          border: '1px solid #6CA6C1',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <label style={{ color: '#F7E9A0', fontSize: '14px' }}>
              Custom Instructions for AI (optional):
            </label>
            <VoiceInputButton onResult={text => setCustomInstructions(prev => prev + ' ' + text)} />
          </div>
          <textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="e.g., Focus on treatment progress, emphasize safety planning, use formal language..."
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              background: '#16325B',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              color: '#f9fafb',
              fontSize: '14px',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
          />
        </div>
      )}

      {/* Expandable Note Type Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {templateKeys.map(template => {
          const isExpanded = expandedTemplate === template;
          const sections = TEMPLATE_SECTIONS[template];
          const data = sectionData[template] || {};
          const hasCont = hasAnySectionContent(template);

          return (
            <div key={template}>
              {/* Template Header — expandable */}
              <button
                onClick={() => toggleTemplate(template)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 20px',
                  background: isExpanded ? '#28527A' : '#1A4D6F',
                  border: 'none',
                  borderBottom: isExpanded ? 'none' : '1px solid #2A5470',
                  borderRadius: isExpanded ? '8px 8px 0 0' : '0',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    color: '#F7E9A0',
                    fontSize: '16px',
                    fontWeight: 600
                  }}>
                    {TEMPLATE_LABELS[template] || template}
                  </span>
                  {hasCont && !isExpanded && (
                    <span style={{
                      background: '#059669',
                      color: '#fff',
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontWeight: 500
                    }}>
                      {getWordCount(template)} words
                    </span>
                  )}
                  {existingNote && existingNote.template === template && (
                    <span style={{
                      background: existingNote.status === 'signed' ? '#059669' : '#D4A017',
                      color: existingNote.status === 'signed' ? '#fff' : '#1f2937',
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontWeight: 600
                    }}>
                      {existingNote.status === 'signed' ? 'Signed' : 'Draft'}
                    </span>
                  )}
                </div>
                <span style={{
                  color: '#F7E9A0',
                  fontSize: '12px',
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'
                }}>
                  ▲
                </span>
              </button>

              {/* Expanded Section Fields */}
              {isExpanded && (
                <div style={{
                  background: '#16325B',
                  border: '1px solid #2A5470',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  padding: '20px'
                }}>
                  {sections.map((section, idx) => (
                    <div key={section.key} style={{ marginBottom: idx < sections.length - 1 ? '20px' : '0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <label
                          htmlFor={`section-${template}-${section.key}`}
                          style={{
                            color: '#87CEEB',
                            fontSize: '14px',
                            fontWeight: 600,
                          }}
                        >
                          {section.label}
                        </label>
                        <VoiceInputButton onResult={text => handleSectionChange(template, section.key, (data[section.key] || '') + ' ' + text)} />
                      </div>
                      <textarea
                        id={`section-${template}-${section.key}`}
                        value={data[section.key] || ''}
                        onChange={(e) => handleSectionChange(template, section.key, e.target.value)}
                        placeholder={section.placeholder}
                        style={{
                          width: '100%',
                          minHeight: '120px',
                          padding: '12px',
                          background: '#0A2540',
                          border: '1px solid #6CA6C1',
                          borderRadius: '6px',
                          color: '#f9fafb',
                          fontSize: '14px',
                          lineHeight: '1.7',
                          resize: 'vertical',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                  ))}

                  {/* Word count + actions for expanded template */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '20px',
                    paddingTop: '16px',
                    borderTop: '1px solid #2A5470'
                  }}>
                    <span style={{ fontSize: '12px', color: '#d1d5db' }}>
                      {getWordCount(template)} words
                    </span>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => handleSave('draft')}
                        disabled={isSaving || !hasCont}
                        style={{
                          background: '#6b7280',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 20px',
                          color: '#fff',
                          fontSize: '14px',
                          cursor: isSaving || !hasCont ? 'not-allowed' : 'pointer',
                          fontWeight: '500',
                          opacity: isSaving || !hasCont ? 0.5 : 1
                        }}
                      >
                        {isSaving ? 'Saving...' : 'Save Draft'}
                      </button>
                      <button
                        onClick={() => handleSave('signed')}
                        disabled={isSaving || !hasCont}
                        style={{
                          background: '#10B981',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 20px',
                          color: '#fff',
                          fontSize: '14px',
                          cursor: isSaving || !hasCont ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          opacity: isSaving || !hasCont ? 0.5 : 1
                        }}
                      >
                        {isSaving ? 'Signing...' : 'Sign & Finalize'}
                      </button>
                      <button
                        onClick={() => { handleExportPDF(false); }}
                        disabled={!hasCont}
                        title="Download as PDF"
                        style={{
                          background: '#2563EB',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 16px',
                          color: '#fff',
                          fontSize: '14px',
                          cursor: !hasCont ? 'not-allowed' : 'pointer',
                          fontWeight: '500',
                          opacity: !hasCont ? 0.5 : 1
                        }}
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => {
                          PDFGeneratorService.fetchPracticeInfo(providerId).then(info => {
                            setExportOverride({
                              practiceName: info.practiceName,
                              providerName: info.providerName,
                              credentials: info.credentials,
                              address: info.address,
                              phone: info.phone,
                              npiNumber: info.npiNumber,
                              licenseNumber: info.licenseNumber,
                              dateOfService: noteDate,
                            });
                            setShowExportModal(true);
                          });
                        }}
                        disabled={!hasCont}
                        title="Export with custom header (for contract work)"
                        style={{
                          background: '#7C3AED',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 16px',
                          color: '#fff',
                          fontSize: '12px',
                          cursor: !hasCont ? 'not-allowed' : 'pointer',
                          fontWeight: '500',
                          opacity: !hasCont ? 0.5 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Custom PDF
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Psychotherapy Note — always separate at bottom */}
      <div style={{
        marginTop: '24px',
        padding: '16px 20px',
        background: '#1A4D6F',
        border: '1px solid #2A5470',
        borderRadius: '8px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <span style={{ color: '#F7E9A0', fontSize: '16px', fontWeight: 600 }}>
              Psychotherapy Note
            </span>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#FCA5A5' }}>
              Kept separate from the client record per HIPAA 45 CFR 164.501
            </p>
          </div>
          <span style={{ color: '#87CEEB', fontSize: '13px', fontStyle: 'italic' }}>
            Coming soon
          </span>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: '#28527A',
        border: '1px solid #6CA6C1',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#d1d5db',
        lineHeight: '1.6'
      }}>
        <strong style={{ color: '#F7E9A0' }}>Important:</strong> AI-generated suggestions are assistive tools only.
        As the licensed provider, you are solely responsible for reviewing, editing, and approving all clinical documentation.
      </div>

      {/* Custom PDF Export Modal */}
      {showExportModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000,
        }} onClick={() => setShowExportModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#1f2937', borderRadius: '12px', border: '1px solid #6CA6C1',
            padding: '28px', width: '520px', maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto',
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#F7E9A0', margin: '0 0 6px 0' }}>
              Customize PDF Header
            </h2>
            <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 20px 0' }}>
              Edit below to export with a different provider or practice header (e.g., contract work for another practice).
            </p>
            {[
              { key: 'practiceName', label: 'Practice Name' },
              { key: 'providerName', label: 'Provider Name' },
              { key: 'credentials', label: 'Credentials (e.g., LPC, NCC)' },
              { key: 'address', label: 'Address' },
              { key: 'phone', label: 'Phone' },
              { key: 'npiNumber', label: 'NPI Number' },
              { key: 'licenseNumber', label: 'License Number' },
              { key: 'dateOfService', label: 'Date of Service' },
            ].map(field => (
              <div key={field.key} style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>
                  {field.label}
                </label>
                <input
                  value={(exportOverride as any)[field.key] || ''}
                  onChange={e => setExportOverride(prev => ({ ...prev, [field.key]: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: '6px',
                    backgroundColor: '#374151', border: '1px solid #6CA6C1',
                    color: 'white', fontSize: '13px', boxSizing: 'border-box',
                  }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setShowExportModal(false)}
                style={{ padding: '10px 20px', backgroundColor: '#4B5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleExportPDF(true)}
                style={{ padding: '10px 20px', backgroundColor: '#7C3AED', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
              >
                Export Custom PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicalNotesEditor;
